import React from 'react';
import { FiLoader, FiCheckCircle, FiXCircle, FiAlertTriangle, FiX } from 'react-icons/fi';

/**
 * Modal component for displaying bulk operation progress
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {number} props.progress - Progress percentage (0-100)
 * @param {number} props.totalDevices - Total number of devices
 * @param {number} props.completedDevices - Number of successfully processed devices
 * @param {number} props.failedDevices - Number of failed devices
 * @param {string} props.status - Status: 'idle' | 'running' | 'completed' | 'partial' | 'failed'
 * @param {string} props.operationName - Name of the operation (e.g., "Sending Commands")
 * @param {Function} props.onClose - Callback when modal is closed
 */
export default function BulkOperationProgress({
    isOpen,
    progress = 0,
    totalDevices = 0,
    completedDevices = 0,
    failedDevices = 0,
    status = 'idle',
    operationName = 'Processing',
    onClose
}) {
    if (!isOpen) return null;

    const isRunning = status === 'running';
    const isCompleted = status === 'completed';
    const isPartial = status === 'partial';
    const isFailed = status === 'failed';

    const getStatusIcon = () => {
        if (isRunning) {
            return <FiLoader className="w-8 h-8 text-blue-400 animate-spin" />;
        }
        if (isCompleted) {
            return <FiCheckCircle className="w-8 h-8 text-green-400" />;
        }
        if (isPartial) {
            return <FiAlertTriangle className="w-8 h-8 text-yellow-400" />;
        }
        if (isFailed) {
            return <FiXCircle className="w-8 h-8 text-red-400" />;
        }
        return null;
    };

    const getStatusText = () => {
        if (isRunning) {
            return `${operationName}...`;
        }
        if (isCompleted) {
            return 'Operation Completed Successfully';
        }
        if (isPartial) {
            return 'Operation Completed with Errors';
        }
        if (isFailed) {
            return 'Operation Failed';
        }
        return operationName;
    };

    const getStatusColor = () => {
        if (isCompleted) return 'from-green-600 to-emerald-600';
        if (isPartial) return 'from-yellow-600 to-orange-600';
        if (isFailed) return 'from-red-600 to-pink-600';
        return 'from-blue-600 to-indigo-600';
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
            <div className="bg-gradient-to-br from-gray-800 to-gray-700 rounded-xl p-6 shadow-2xl w-full max-w-md mx-4">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        {getStatusIcon()}
                        <h3 className="text-xl font-semibold text-white">
                            {getStatusText()}
                        </h3>
                    </div>
                    {!isRunning && (
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-white transition-colors"
                        >
                            <FiX className="w-5 h-5" />
                        </button>
                    )}
                </div>

                {/* Progress Bar */}
                <div className="mb-6">
                    <div className="flex justify-between text-sm text-gray-400 mb-2">
                        <span>Progress</span>
                        <span>{Math.round(progress)}%</span>
                    </div>
                    <div className="w-full h-3 bg-gray-600 rounded-full overflow-hidden">
                        <div
                            className={`h-full bg-gradient-to-r ${getStatusColor()} transition-all duration-300 ease-out`}
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-white">{totalDevices}</p>
                        <p className="text-xs text-gray-400">Total Devices</p>
                    </div>
                    <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-green-400">{completedDevices}</p>
                        <p className="text-xs text-gray-400">Successful</p>
                    </div>
                    <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                        <p className={`text-2xl font-bold ${failedDevices > 0 ? 'text-red-400' : 'text-gray-500'}`}>
                            {failedDevices}
                        </p>
                        <p className="text-xs text-gray-400">Failed</p>
                    </div>
                </div>

                {/* Status Message */}
                {isRunning && (
                    <p className="text-sm text-gray-400 text-center">
                        Please wait while the operation completes. Do not close this window.
                    </p>
                )}

                {isPartial && (
                    <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-3">
                        <p className="text-sm text-yellow-300">
                            Some devices could not be updated. They may be offline or have connection issues.
                            The operation will be retried automatically when devices come online.
                        </p>
                    </div>
                )}

                {isFailed && (
                    <div className="bg-red-900/30 border border-red-700 rounded-lg p-3">
                        <p className="text-sm text-red-300">
                            The operation failed. Please check your connection and try again.
                        </p>
                    </div>
                )}

                {/* Close Button */}
                {!isRunning && (
                    <button
                        onClick={onClose}
                        className={`w-full mt-4 py-2 px-4 rounded-lg font-medium transition-all transform hover:scale-105 bg-gradient-to-r ${getStatusColor()} text-white`}
                    >
                        Close
                    </button>
                )}
            </div>
        </div>
    );
}
